import React, { useState, useEffect } from 'react';
import { getFileDownloadURL } from '../firebase/r2storage';
import { extractFileKeyFromUrl, isUrlExpired } from '../utils/urlRefreshHelper';

/**
 * Component to display images from R2 with automatic presigned URL generation
 * Usage: <R2Image fileKey="student_documents/userId/file.png" alt="Document" className="w-32 h-32" />
 */
export default function R2Image({ fileKey, alt = '', className = '', fallback = null }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!fileKey) {
      setLoading(false);
      setError(true);
      return;
    }

    // Generate presigned URL for R2
    const loadImage = async () => {
      try {
        setLoading(true);
        
        let actualFileKey = fileKey;
        
        // If it's a URL, check if it's expired and extract the file key
        if (fileKey.startsWith('http://') || fileKey.startsWith('https://')) {
          const expired = isUrlExpired(fileKey);
          
          // If not expired, use it directly
          if (!expired) {
            setImageUrl(fileKey);
            setLoading(false);
            return;
          }
          
          // If expired, extract the file key to regenerate
          actualFileKey = extractFileKeyFromUrl(fileKey);
          if (!actualFileKey) {
            console.error('Could not extract file key from URL:', fileKey);
            setError(true);
            setLoading(false);
            return;
          }
        }
        
        // Generate fresh presigned URL
        const result = await getFileDownloadURL(actualFileKey);
        
        if (result.success) {
          setImageUrl(result.url);
          setError(false);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error loading R2 image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [fileKey]);

  if (loading) {
    return (
      <div className={`${className} bg-gray-200 animate-pulse flex items-center justify-center`}>
        <span className="text-gray-400 text-xs">Loading...</span>
      </div>
    );
  }

  if (error || !imageUrl) {
    return fallback || (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <span className="text-gray-400 text-xs">No image</span>
      </div>
    );
  }

  return (
    <img 
      src={imageUrl} 
      alt={alt} 
      className={className}
      onError={() => setError(true)}
    />
  );
}
